import { company, type PhoneId, type PhoneNumber } from '@/config/company';

/**
 * Which of the published numbers belong in which kind of control.
 *
 * One module so the rule is written once. Before this, "a number a visitor
 * may ring" was spelled `country === 'Uganda'` in the header strip, the scroll
 * ribbon and the free-advice rail — three copies of a guess that was only
 * accidentally right, and that each would have had to be found and corrected
 * the day a callable foreign number or an uncallable local one was added.
 * Now there is one predicate, and it reads the intent stated on the number
 * itself (`channels` in `config/company.ts`).
 *
 * The lists are module constants rather than hook results: `company.phones` is
 * static config, so filtering it per render would allocate a new array every
 * time for an answer that cannot change.
 */

/** A number a visitor may ring. Goes in a `tel:` link. */
export const isCallable = (phone: PhoneNumber): boolean => phone.channels.includes('call');

/** A number that takes WhatsApp messages, whether or not it also takes calls. */
export const isWhatsAppable = (phone: PhoneNumber): boolean => phone.channels.includes('whatsapp');

/**
 * A number that must never be offered as a call.
 *
 * The one that needs explaining to a visitor, and the reason `note` exists:
 * a number printed without a `tel:` link looks like an oversight unless the
 * page says why it is not one.
 */
export const isWhatsAppOnly = (phone: PhoneNumber): boolean =>
  isWhatsAppable(phone) && !isCallable(phone);

/** Every line that can be rung, in the order the config lists them. */
export const callablePhones: readonly PhoneNumber[] = company.phones.filter(isCallable);

/** The first line to offer when a control has room for exactly one number. */
export const primaryCallablePhone: PhoneNumber | undefined = callablePhones[0];

/** One published line by id, or undefined if it has been removed from the config. */
export const findPhone = (id: PhoneId): PhoneNumber | undefined =>
  company.phones.find((phone) => phone.id === id);
